import SafeArea from "@/components/safe-area";
import { Bots, BridgeCallHandlers, BridgeClientShareTypes } from "@/interfaces/enums";
import bridge from "@/utils/dsBridge";
import rfStore from "@/utils/rfStore";
import { isInApp } from "@/utils/util";
import {
	ChevronLeftIcon,
	PlusIcon,
	ChevronDown,
	ArrowUpRightFromCircle,
	ChevronRight,
	LogIn,
	LogOut,
} from "lucide-react";
import { useEffect, useState } from "react";
import ModalLoginView from "../common/loginView/ModalLogin";
import rfLocalStore from "@/utils/rfLocalStore";
import DropSection from "./views/DropSection";
import DropItem from "./views/DropItem";
import { useTranslation } from "next-i18next";
import { MoreIcon } from "@/components/RFIcon";

interface Props {
	onCreate: () => void;
	bot: Bots;
	botName: string;
	loading: boolean;
	botOptions: {
		label: string;
		value: Bots;
		disabled: boolean;
		tag: string;
		icon: string;
	}[];
	onBotChange: (bot: Bots) => void;
	locale: string;
}

export default function HeaderView(props: Props) {
	const { t } = useTranslation("copilot");

	const [visible, setVisible] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const [inApp, setInApp] = useState(false);
	const [isLogin, setIsLogin] = useState(false);
	useEffect(() => {
		setInApp(isInApp());
		setIsLogin(!!rfStore.getRFState()?.userId);
	}, []);

	const onLoginSuccess = () => {
		window.location.reload();
	};

	const handleLogout = () => {
		rfLocalStore.logout();
		setIsOpen(false);
		window.location.reload();
	};

	const handleClose = () => {
		bridge.call(BridgeCallHandlers.ON_CLOSE_WEB_PAGE);
	};
	const handleShare = () => {
		setIsOpen(false);
		bridge?.call(BridgeCallHandlers.ON_SHARE_BUTTON_CLICK, {
			shareType: BridgeClientShareTypes.TEXT,
			shareTitle: t("SHARE_TITLE") + "\n" + shareLink(),
			shareUrl: shareLink(),
			shareImgUrl: "",
			shareCode: "",
			shareScene: "",
			showCopyButton: 1,
		});
	};
	const shareLink = () => {
		const origin = window.location.origin.replace(".ai", ".tech");

		return `${origin}/${props.locale}/copilot`;
	};

	useEffect(() => {
		const handler = () => {
			setIsOpen(false);
		};
		document.addEventListener("click", handler);
		return () => {
			document.removeEventListener("click", handler);
		};
	}, []);

	return (
		<div className="flex-shrink-0 bg-white p-2">
			<SafeArea position="top" />

			<ModalLoginView visible={visible} onClose={() => setVisible(false)} onLoginSuccess={onLoginSuccess} />

			<div className="flex items-center">
				<div className="m-4 inline-flex flex-shrink-0" onClick={handleClose}>
					{inApp && <ChevronLeftIcon size={24} />}
				</div>

				<div className="flex-1 text-center">
					<span className="text-base font-semibold text-[#19003E]">{props.botName}</span>
				</div>

				<div className="inline-flex min-w-[32px] flex-shrink-0">
					<div className="relative">
						<div
							className="flex h-[34px] w-14 cursor-pointer items-center justify-center rounded-[40px] bg-black/[4%]"
							onClick={(e) => {
								e.stopPropagation();
								setIsOpen(true);
							}}
						>
							<MoreIcon />
						</div>
						{isOpen && (
							<div className="absolute right-0 top-[100%] mt-3 shadow-medium rounded-lg px-3">
								<div className="w-[168px]">
									<DropSection
										title={t("switch-bot")}
										defaultIcon={<ChevronRight />}
										activeIcon={<ChevronDown />}
										showDivider
										groups={
											<>
												{props.botOptions.map((bot) => (
													<DropItem
														key={bot.value}
														title={bot.label}
														tag={bot.tag}
														icon={bot.icon}
														onClick={() => {
															if (bot.value === props.bot) return;
															setIsOpen(false);
															props.onBotChange(bot.value);
														}}
														active={bot.value === props.bot}
													/>
												))}
											</>
										}
									/>
									<DropSection
										title={t("new-chat")}
										defaultIcon={<PlusIcon />}
										showDivider
										onClick={() => {
											setIsOpen(false);
											props.onCreate();
										}}
									/>

									{inApp && (
										<DropSection
											title={t("share-text")}
											defaultIcon={<ArrowUpRightFromCircle />}
											showDivider={false}
											onClick={handleShare}
										/>
									)}

									{!inApp && (
										<>
											{isLogin ? (
												<DropSection
													title={t("log-out", { ns: "login" })}
													defaultIcon={<LogOut />}
													active
													showDivider={false}
													onClick={handleLogout}
												/>
											) : (
												<DropSection
													title={t("log-in", { ns: "login" })}
													defaultIcon={<LogIn />}
													active
													showDivider={false}
													onClick={() => {
														if (props.loading) return;
														setIsOpen(false);
														setVisible(true);
													}}
												/>
											)}
										</>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
